import * as React from 'react';
import { useImperativeHandle } from 'react';
import { Typography, ListItem, ListItemText, ListItemIcon, ListItemButton } from '@mui/material';
import { Box } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useInvoices } from '../../../hooks/invoice/useInvoices';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InvoiceView from '../modals/InvoiceView';
import { useDeleteInvoice } from '../../../hooks/invoice/useDeleteInvoice';
import { getInvoiceById } from '../../../api/invoiceService';
import { numberToCurrencyFormat } from '../../../utils/numberToCurrencyFormat';
import InvoiceForm from '../forms/InvoiceForm';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function InvoicesListItems({ setListEmpty = null, setDoneLoading = null, history = null, historyEntry = null, pet = null, mode = 'history', upcoming = false, ref }) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const  {invoices, loading, loadMore, searchFunction, resetParent } = useInvoices(history?.id, historyEntry?.id, pet?.id, mode, upcoming);

  const [selectedInvoice, setSelectedInvoice] = React.useState(null);

  const [invoiceFormOpen, setInvoiceFormOpen] = React.useState(false);
  const [invoiceFormMode, setInvoiceFormMode] = React.useState('create');
  const [invoiceViewOpen, setInvoiceViewOpen] = React.useState(false);

  const onInvoiceSaveSuccess = async (updatedRecord) => {
    const updatedInvoice = await getInvoiceById(updatedRecord.id);
    setSelectedInvoice(updatedInvoice);
    resetParent();
    if (invoiceFormMode === 'create')
    {
      setInvoiceViewOpen(true);
    }
  };

  const onInvoiceItemSaveSuccess = async (updatedInvoiceItems) => {
    const updatedInvoice = await getInvoiceById(updatedInvoiceItems[0].invoice_id);
    setSelectedInvoice(updatedInvoice);
    resetParent();
  };

  const onInvoicePaymentSaveSuccess = async (updatedInvoicePayment) => {
    const updatedInvoice = await getInvoiceById(updatedInvoicePayment.invoice_id);
    setSelectedInvoice(updatedInvoice);
    resetParent();
  };

  const onInvoiceItemDeleteSuccess = async (invoice) => {
    const updatedInvoice = await getInvoiceById(invoice.id);
    setSelectedInvoice(updatedInvoice);
    resetParent();
  };

  const handleInvoiceCreate = () => {
    setSelectedInvoice(null);
    setInvoiceFormMode('create');
    setInvoiceFormOpen(true);
  };
  // Expose the function to parent
  useImperativeHandle(ref, () => ({
    handleInvoiceCreate
  }));

  const handleInvoiceEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceFormMode('edit');
    setInvoiceFormOpen(true);
  };
  const handleInvoiceDelete = async (invoice) => {
    try {
      await remove(invoice.id);
    } catch (err) {
      if (err.status === 409)
      {
        showSnackbar(t('invoice.messages.delete_fail_has_records'), "error");
      }
      else {
        showSnackbar(t('invoice.messages.delete_fail'), "error");
      }
    }
    
  };
  const onDeleteInvoiceSuccess = () => {
    resetParent();
    showSnackbar(t('invoice.messages.delete_success'), "success");
  };
  const { remove } = useDeleteInvoice(onDeleteInvoiceSuccess);

  const handleListItemClick = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceViewOpen(true);
  };

  const handleListItemEdit = (invoice) => {
    handleInvoiceEdit(invoice);
  };

  const handleListItemDelete = (invoice) => {
    handleInvoiceDelete(invoice);
  };

  const statusToColor = (status) => {
    let color = "";
    switch (status) {
      case "pending":
        color = "error";
        break;
      case "partially paid":
        color = "warning";
        break;
      case "paid":
        color = "success";
        break;
      case "overdue":
        color = "error";
        break;
      case "void":
        color = "grey";
        break;
      default:
        color = "grey";
    }
    return color;
  }

  React.useEffect(() => {
    if (invoices.data.length !== 0)
    {
      if (setListEmpty) setListEmpty(false);
    }
  }, [invoices]);

  React.useEffect(() => {
    if (!loading)
    {
      if (setDoneLoading) setDoneLoading(true);
    }
  }, [loading]);

  return (
    <>
      {loading ? (
        <></>
      ) : (
      invoices?.data.map((invoice) => (
        <ListItem 
            key={"invoice" + invoice.id}
            sx={{ py: 0 }}
            alignItems="flex-start"
            secondaryAction={
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <VerticalHamburgerMenu
                    options={[
                        { 
                            id: 1, 
                            name: 'View',
                            callback: () => handleListItemClick(invoice),
                            disabled: !userHasAccess(SUB_MODULES.INVOICE, 1)
                        },
                        { 
                            id: 2, 
                            name: 'Edit',
                            callback: () => handleListItemEdit(invoice),
                            disabled: !userHasAccess(SUB_MODULES.INVOICE, 2)
                        },
                        {
                            id: 3,
                            name: 'Delete',
                            callback: () => handleListItemDelete(invoice),
                            confirmMessage: t('invoice.messages.delete_confirm', { name: invoice.invoice_number }),
                            disabled: !userHasAccess(SUB_MODULES.INVOICE, 3)
                        }
                    ]}
                    />
                </Box>
            }
        >
            <ListItemButton 
              sx={{ py: 0 }} 
              onClick={() => {if (userHasAccess(SUB_MODULES.INVOICE, 1)) handleListItemClick(invoice)}}
            >
            <ListItemIcon>
              <AttachMoneyIcon color='primary' />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                <Typography variant="body2" sx={{ maxWidth: '90%' }}>
                  {`Tot${numberToCurrencyFormat(invoice.total_amount)} Pd${numberToCurrencyFormat(invoice.calculated_total_paid)} Bal${numberToCurrencyFormat(invoice.calculated_balance_remaining)}`}
                </Typography>
                </>
              }
              secondary={
                <Typography color={statusToColor(invoice.status)} variant="body2" sx={{ my: 0, maxWidth: '90%' }}>
                  {`${invoice.status}`}
                </Typography>
              }
            />
            </ListItemButton>
        </ListItem>
      )))}
      <InvoiceForm
        mode={invoiceFormMode}
        invoice={selectedInvoice}
        successCallback={onInvoiceSaveSuccess}
        history={history}
        historyEntry={historyEntry}
        pet={pet} 
        open={invoiceFormOpen}
        onClose={() => setInvoiceFormOpen(false)}
      />
      <InvoiceView 
        successCallback={onInvoiceSaveSuccess}
        onInvoiceItemSaveSuccess={onInvoiceItemSaveSuccess}
        onInvoiceItemDeleteSuccess={onInvoiceItemDeleteSuccess}
        onInvoicePaymentSaveSuccess={onInvoicePaymentSaveSuccess}
        handleEdit={handleInvoiceEdit}
        invoice={selectedInvoice}
        open={invoiceViewOpen}
        onClose={() => setInvoiceViewOpen(false)}
      />
    </>
  );
}